//#region types
import { SalaryDeviationFormTypes } from "models/formTypes";
import { CompensationFormViemProps } from "models/propTypes/wintSalaryPropTypes";
import { DateInputProps, SearchInputProps, TimeInputProps, WintFormConnect } from "models/propTypes/wintFormPropsTypes";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import React, { useEffect, useMemo } from "react";
import { Grid } from "@mui/material";
import {
  CheckboxInput,
  DateInput,
  EditableTable,
  FormFieldLayout,
  SearchInput,
  TimeInput,
  VerticalFormField,
} from "components/common/wintForms";
//#endregion
import { CountryHelper, DateHelper, LocalizationHelperInstance, WorkerHelper } from "utils/helpers";
import SalaryDeviationHelper from "./salaryDeviationHelper";

// Wrapper component that disables DateInput when it has a value
const DisabledWhenPopulatedDateInput: React.FC<DateInputProps> = (props) => {
  return <DateInput {...props} disabled={props.disabled || !!props.value} />;
};

const TravelCompensationSection: React.FC<CompensationFormViemProps> = (props) => {
  const { connect, values, readOnly } = props;

  const mealDeductionsConnector = connect("travelCompensation.mealDeductions");
  const countryCodeConnector = connect("travelCompensation.countryCode");
  const fromDateConnector = connect("travelCompensation.fromDate");
  const toDateConnector = connect("travelCompensation.toDate");

  const mealDatesWorkerInstance = useMemo(() => WorkerHelper.getSalaryDeviationMealDatesWorker(), []);

  const previousMonthDate = useMemo(() => {
    return DateHelper.getFirstDayOfPreviousMonth(new Date());
  }, []);

  // Dynamic initial focused date based on the other field's selection
  const fromDateInitialFocus = toDateConnector.value instanceof Date ? toDateConnector.value : previousMonthDate;
  const toDateInitialFocus = fromDateConnector.value instanceof Date ? fromDateConnector.value : previousMonthDate;

  useEffect(() => {
    const startDate = fromDateConnector.value;
    const endDate = toDateConnector.value;

    if (!startDate || !endDate) return;

    const handleWorkerMessage = (e: MessageEvent<SalaryDeviationFormTypes.SalaryDeviationMeal[]>) => {
      mealDeductionsConnector.setValue(e.data);
    };

    mealDatesWorkerInstance.onmessage = handleWorkerMessage;

    mealDatesWorkerInstance.postMessage({
      startDate,
      endDate,
      existingDayHours: values.travelCompensation?.mealDeductions,
    });

    return () => {
      mealDatesWorkerInstance.onmessage = null;
    };
  }, [
    fromDateConnector.value,
    toDateConnector.value,
    values.deviation?.data?.informationType,
    values.deviation?.data?.category,
  ]);

  const isInternational = SalaryDeviationHelper.isInternationalTravelCompensation(values.deviation);

  const countryList = useMemo(() => {
    return CountryHelper.getCountryAutoCompleteList()
      .filter((country) => country.id !== "SE")
      .sort((a, b) => a.value.localeCompare(b.value));
  }, []);

  return (
    <>
      {isInternational && (
        <VerticalFormField<SearchInputProps>
          Component={SearchInput}
          {...countryCodeConnector}
          label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Country)}
          items={countryList}
          showMainLabel
          disabled={readOnly}
        />
      )}
      <Grid container direction="row" spacing={1}>
        <Grid item xs={6}>
          <VerticalFormField<DateInputProps>
            Component={DateInput}
            {...fromDateConnector}
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.DateFrom)}
            icon={IconDefinitionConstants.WCalendar}
            initialFocusedDate={fromDateInitialFocus}
            disabled={readOnly}
          />
        </Grid>
        <Grid item xs={6}>
          <VerticalFormField<TimeInputProps>
            Component={TimeInput}
            {...connect("travelCompensation.fromTime")}
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.FromTime)}
            disabled={readOnly}
          />
        </Grid>
      </Grid>
      <Grid container direction="row" spacing={1}>
        <Grid item xs={6}>
          <VerticalFormField<DateInputProps>
            Component={DateInput}
            {...toDateConnector}
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.DateTo)}
            icon={IconDefinitionConstants.WCalendar}
            initialFocusedDate={toDateInitialFocus}
            disabled={readOnly}
          />
        </Grid>
        <Grid item xs={6}>
          <VerticalFormField<TimeInputProps>
            Component={TimeInput}
            {...connect("travelCompensation.toTime")}
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.ToTime)}
            disabled={readOnly}
          />
        </Grid>
      </Grid>
      <FormFieldLayout {...mealDeductionsConnector} direction="vertical" hideMainLabel>
        <EditableTable<SalaryDeviationFormTypes.SalaryDeviationMeal>
          connect={connect as WintFormConnect<any>}
          name={"travelCompensation.mealDeductions"}
          value={values.travelCompensation?.mealDeductions ?? []}
          fields={{
            day: {
              Component: DisabledWhenPopulatedDateInput,
              extraProps: {
                label: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.DayPascal),
                headerLabel: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Allowance.MealTitle),
                minDate: values.travelCompensation?.fromDate,
                maxDate: values.travelCompensation?.toDate,
                disabled: readOnly,
              },
            },
            breakfast: {
              Component: CheckboxInput,
              extraProps: {
                headerLabel: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.Breakfast),
                disabled: readOnly,
              },
            },
            lunch: {
              Component: CheckboxInput,
              extraProps: {
                headerLabel: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.Lunch),
                disabled: readOnly,
              },
            },
            dinner: {
              Component: CheckboxInput,
              extraProps: {
                headerLabel: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.Dinner),
                disabled: readOnly,
              },
            },
          }}
          idField="id"
          defaultRow={{ day: undefined, breakfast: false, lunch: false, dinner: false }}
          showColumnHeaders
          hideActions={readOnly}
        />
      </FormFieldLayout>
    </>
  );
};

export default TravelCompensationSection;
